// src/pages/ApplicationChampionship/components/ChampionshipCreateModal/index.tsx
import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
  FormControl,
  Switch,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePostChampionshipMutation } from '@/services/queries/championship';
import { PostChampionshipRequest } from '@/services/request/championship';

export interface ChampionshipCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const ChampionshipCreateModal = ({ isOpen, onClose, onDone }: ChampionshipCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostChampionshipMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      name: '',
      is_filtered: false,
      is_active: true,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostChampionshipRequest = {
        name: data.name,
        is_filtered: data.is_filtered,
        is_active: data.is_active,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_championship.components.championship_create_modal.championship_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_championship.components.championship_create_modal.championship_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_championship.components.championship_create_modal.championship_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.application_championship.components.championship_create_modal.championship_create_name')}
              {...register('name')}
              helperText={t(
                'pages.application_championship.components.championship_create_modal.championship_create_name_limits',
              )}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
            />

            <Flex direction='column' gap={2}>
              <Controller
                name='is_active'
                control={control}
                render={({ field }) => (
                  <FormControl display='flex' gap={2} alignItems='center'>
                    <Switch
                      name={field.name}
                      ref={field.ref}
                      isChecked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                    <Text fontWeight='medium'>
                      {t(
                        'pages.application_championship.components.championship_create_modal.championship_create_is_active_helper',
                      )}
                    </Text>
                  </FormControl>
                )}
              />
            </Flex>

            <Flex direction='column' gap={2}>
              <Controller
                name='is_filtered'
                control={control}
                render={({ field }) => (
                  <FormControl display='flex' gap={2} alignItems='center'>
                    <Switch
                      name={field.name}
                      ref={field.ref}
                      isChecked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                    <Text fontWeight='medium'>
                      {t(
                        'pages.application_championship.components.championship_create_modal.championship_create_is_filtered_helper',
                      )}
                    </Text>
                  </FormControl>
                )}
              />
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='space-between' gap='kilo'>
          <Flex w='100%' justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_championship.components.championship_create_modal.championship_create_cancel')}
            </Button>
            <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
              {t('pages.application_championship.components.championship_create_modal.championship_create_create')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isPending} />
    </Modal>
  );
};
